import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, Table } from "react-bootstrap";
import { Helmet } from "react-helmet";
import AxiosController from "../../../authentication/AxiosController";
import Loader from "../../../loader/Loader";
import IconsGen from "../../../icons_generator/IconsGen";
import CreateAndUpdateCategoryOffer from "../../../modals/CreateAndUpdateCategoryOffer";

const CategoryOffers = () => {
  const { slug, lang } = useParams();
  const [_] = useTranslation("global");
  const box_head_dir = lang === "en" ? "ltr" : "rtl";
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMethod, setModalMethod] = useState("add");
  const [selectedOffer, setSelectedOffer] = useState(null);

  const getCategoryOffers = useCallback(() => {
    setLoading(true);
    AxiosController.get(`/api/${slug}/${lang}/category-offers`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        setLoading(false);
        setAllData(res?.data?.data || res?.data || []);
      })
      .catch(() => {
        setLoading(false);
        setAllData([]);
      });
  }, [slug, lang, token]);

  const getCategories = useCallback(() => {
    AxiosController.get(`/api/${slug}/${lang}/categories?all=1`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        setCategories(res?.data || []);
      })
      .catch(() => {
        setCategories([]);
      });
  }, [slug, lang, token]);

  useEffect(() => {
    getCategoryOffers();
    getCategories();
  }, [getCategoryOffers, getCategories]);

  const handleAddClick = () => {
    setModalMethod("add");
    setSelectedOffer(null);
    setShowModal(true);
  };

  const handleEditClick = (offer) => {
    setModalMethod("edit");
    setSelectedOffer(offer);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedOffer(null);
  };

  const handleModalSuccess = () => {
    getCategoryOffers();
    setShowModal(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString(lang === "ar" ? "ar-EG" : "en-US", {
      timeZone: "Asia/Dubai",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getOfferTypeLabel = (type) => {
    switch (type) {
      case "fixed_price":
        return _("panel.fixedPrice");
      case "percentage":
        return _("panel.percentage");
      case "fixed_discount":
        return _("panel.fixedDiscount");
      default:
        return type;
    }
  };

  const getResponsibilityLabel = (resp) => {
    switch (resp) {
      case "system":
        return _("panel.system");
      case "seller":
        return _("panel.seller");
      default:
        return resp;
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category
      ? category.arabic_name || category.english_name || category.name
      : categoryId;
  };

  return (
    <div className="flex-grow-1 stats-component" dir={box_head_dir}>
      <Helmet>
        <title>{_("panel.categoryOffers")}</title>
      </Helmet>
      <Loader active={loading} />
      
      <div className="d-flex flex-column flex-md-row py-2 justify-content-between flex-wrap align-items-center">
        <h3 className="fs-2 mb-4" style={{ color: "rgba(89, 198, 201, 1)" }}>
          {_("panel.categoryOffers")}
        </h3>
        <Button
          className="fs-5 px-5 mb-4"
          style={{
            backgroundColor: "rgb(89, 198, 201)",
            border: "none",
            borderRadius: "7px",
          }}
          onClick={handleAddClick}
        >
          {_("panel.add")}
        </Button>
      </div>

      <div className="my-3 d-flex flex-column gap-2 px-2 position-relative">
        <div className="row">
          <div
            style={{ borderRadius: "40px" }}
            className={`table-responsive border my-3 d-flex ${
              lang === "en" ? "flex-row" : "flex-row-reversed"
            } align-items-center`}
          >
            <Table responsive className="table table-borderless">
              <thead>
                <tr className="text-center">
                  <th>#</th>
                  <th>{_("panel.offerName")}</th>
                  <th>{_("panel.category")}</th>
                  <th>{_("panel.offerType")}</th>
                  <th>{_("panel.offerValue")}</th>
                  <th>{_("panel.responsibility")}</th>
                  <th>{_("panel.validFrom")}</th>
                  <th>{_("panel.validUntil")}</th>
                  <th>{_("panel.status")}</th>
                  <th>{_("panel.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {allData.length > 0 ? (
                  allData.map((offer, index) => (
                    <tr key={offer.id} className="text-center">
                      <td>{index + 1}</td>
                      <td>{offer.name}</td>
                      <td>{getCategoryName(offer.category_id)}</td>
                      <td>{getOfferTypeLabel(offer.offer_type)}</td>
                      <td>
                        {offer.offer_type === "percentage" 
                          ? `${offer.offer_value}%` 
                          : offer.offer_value}
                      </td>
                      <td>{getResponsibilityLabel(offer.responsibility)}</td>
                      <td>{formatDate(offer.valid_from)}</td>
                      <td>{formatDate(offer.valid_until)}</td>
                      <td>
                        <span
                          className={`badge ${
                            offer.is_active ? "bg-success" : "bg-secondary"
                          }`}
                        >
                          {offer.is_active
                            ? _("panel.Active")
                            : _("panel.Inactive")}
                        </span>
                      </td>
                      <td>
                        <div
                          className="d-flex justify-content-center gap-2"
                          style={{ cursor: "pointer" }}
                        >
                          <span onClick={() => handleEditClick(offer)}>
                            <IconsGen icon_name="edit" />
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center">
                      {_("panel.noData")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      <CreateAndUpdateCategoryOffer
        method={modalMethod}
        show={showModal}
        offerData={selectedOffer}
        categories={categories}
        handleModalClose={handleModalClose}
        handleModalSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default CategoryOffers;
